// 005 — Evolución y Prescripciones (fila 4 de 8.3). Híbrida: ClinicalImpression
// (nota de evolución diaria) + prescripciones. Fase 5 reemplaza la creación
// cruda de MedicationRequest por el wizard de receta (firma electrónica + QR
// de validación pública, ver ../recetas/RecetaWizard.tsx) — cada prescripción
// pasa por su propio wizard completo, no un batch al guardar la nota.
//
// Puerto a shadcn/Tailwind de packages/hce/src/hce/forms/Formulario005.tsx:
// el `Modal` de Mantine que envolvía el wizard pasa a ser un `Dialog` de
// shadcn. Cada guardado exitoso (nota o receta) reporta sus pares
// pregunta/respuesta vía `onGuardado` — contrato nuevo de
// FormularioDiscretoProps (ver ./index.ts) — aunque la receta ya tiene su
// PROPIA firma (RecetaWizard), así que en la práctica esto solo actualiza el
// estado "guardado" visual de la página contenedora, sin ofrecer una segunda
// firma redundante.
//
// Fase 7 (Tanda 16): bloque A del papel (`EncabezadoMspCampos`), fecha y hora de la evolución visibles y editables
// (precargadas con el momento actual) y "Farmacoterapia e indicaciones" para indicaciones no medicamentosas: van SOLO en
// el PDF firmado (decisión de Sue: no se crean recursos, para no contaminar Kardex/Farmacia). La columna "Administr.
// fármacos / dispositivo" del papel la registra enfermería en 022; `RecetaWizard`/`construirReceta` no se tocan y la
// nota de evolución sigue creando un `ClinicalImpression` `completed` con `encounter` (completitud y calidad HC).
import type { MedicationRequest } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { FormularioDiscretoProps, Par } from './index';
import { AreaDictado } from './AreaDictado';
import { paresDePrescripciones } from './prescripcion005';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { RecetaWizard } from '../recetas/RecetaWizard';
import { puedeAccionProfesional, useRolDelPracticante } from '@/lib/useRolDelPracticante';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';

const dos = (n: number): string => String(n).padStart(2, '0');
const fechaLocal = (d: Date): string => `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`;
const horaLocal = (d: Date): string => `${dos(d.getHours())}:${dos(d.getMinutes())}`;

export function Formulario005({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  // BA-C-11: solo médico y odontólogo prescriben (el servidor responde 403 al resto); se oculta el botón que no podrían usar.
  const puedePrescribir = puedeAccionProfesional(useRolDelPracticante(), 'prescribir');
  const { t } = useTranslation();
  const medplum = useMedplum();
  const [notaEvolucion, setNotaEvolucion] = useState('');
  const [indicaciones, setIndicaciones] = useState('');
  const [fecha, setFecha] = useState(() => fechaLocal(new Date()));
  const [hora, setHora] = useState(() => horaLocal(new Date()));
  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, '005', t('formularios.f005.titulo'));
  const { guardando, error, guardar } = useGuardado();
  const [recetasEmitidas, setRecetasEmitidas] = useState<MedicationRequest[]>([]);
  const [wizardAbierto, setWizardAbierto] = useState(false);
  // El documento firmado es UNO solo (modo `msp`): la última nota guardada + todas las prescripciones emitidas en esta sesión.
  const [paresNota, setParesNota] = useState<Par[]>([]);
  const [paresRecetas, setParesRecetas] = useState<Par[]>([]);

  const hayContenido = notaEvolucion.trim() !== '' || indicaciones.trim() !== '';

  const guardarNota = (): Promise<void> => guardar(async () => {
    if (!hayContenido) return;
    const momento = fecha ? new Date(`${fecha}T${hora || '00:00'}:00`) : new Date();
    if (notaEvolucion.trim()) {
      await medplum.createResource({
        resourceType: 'ClinicalImpression',
        status: 'completed',
        subject,
        encounter,
        date: momento.toISOString(),
        summary: notaEvolucion.trim(),
      });
    }
    const nuevosParesNota = [
      { pregunta: t('formularios.f005.fecha'), respuesta: fecha },
      { pregunta: t('formularios.f005.hora'), respuesta: hora },
      { pregunta: t('formularios.f005.notaEvolucion'), respuesta: notaEvolucion.trim() },
      { pregunta: t('formularios.f005.indicaciones'), respuesta: indicaciones.trim() },
    ].filter((p) => p.respuesta.trim() !== '');
    // Cada guardado agrega su nota al documento (no reemplaza a la anterior).
    const acumuladosNota = [...paresNota, ...nuevosParesNota];
    setParesNota(acumuladosNota);
    onGuardado([...acumuladosNota, ...paresRecetas], datosEncabezadoMsp);
    setNotaEvolucion('');
    setIndicaciones('');
  });

  return (
    <div className="flex flex-col gap-4">
      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f005.evolucionTitulo')}</h4>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="flex flex-col gap-1">
          <Label htmlFor="f005-fecha">{t('formularios.f005.fecha')}</Label>
          <Input id="f005-fecha" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor="f005-hora">{t('formularios.f005.hora')}</Label>
          <Input id="f005-hora" type="time" value={hora} onChange={(e) => setHora(e.target.value)} />
        </div>
      </div>
      <AreaDictado id="f005-nota-evolucion" etiqueta={t('formularios.f005.notaEvolucion')} valor={notaEvolucion} onChange={setNotaEvolucion} filas={4} />

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f005.indicacionesTitulo')}</h4>
      <div className="flex flex-col gap-1">
        <AreaDictado id="f005-indicaciones" etiqueta={t('formularios.f005.indicaciones')} valor={indicaciones} onChange={setIndicaciones} filas={4} />
        <p className="text-xs text-hce-muted">{t('formularios.f005.indicacionesNota')}</p>
      </div>
      <ErrorGuardado error={error} />
      <div className="flex justify-end">
        <Button type="button" onClick={() => void guardarNota()} disabled={!hayContenido || guardando}>
          {t('formularios.f005.guardarNota')}
        </Button>
      </div>

      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f005.prescripciones')}</h4>
      {recetasEmitidas.length === 0 ? (
        <p className="text-sm text-hce-muted">{t('formularios.f005.sinRecetas')}</p>
      ) : (
        <ul className="flex flex-col gap-1 text-sm text-hce-ink">
          {recetasEmitidas.map((mr) => (
            <li key={mr.id} className="list-disc pl-4">
              {mr.medicationCodeableConcept?.text}
            </li>
          ))}
        </ul>
      )}
      {puedePrescribir && (
        <Button type="button" variant="outline" className="w-fit" onClick={() => setWizardAbierto(true)}>
          {t('formularios.f005.prescribirMedicamento')}
        </Button>
      )}

      <Dialog open={wizardAbierto} onOpenChange={setWizardAbierto}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('receta.tituloWizard')}</DialogTitle>
          </DialogHeader>
          {wizardAbierto && (
            <RecetaWizard
              subject={subject}
              encounter={encounter}
              onCreada={(mrs, vias) => {
                setRecetasEmitidas((prev) => [...prev, ...mrs]);
                setWizardAbierto(false);
                // Antes esto reemplazaba el documento por uno genérico solo con la DCI: se perdían la nota y el encabezado MSP.
                const nuevos = paresDePrescripciones(mrs, vias, paresRecetas.length, {
                  prescripcion: t('formularios.f005.prescripcionNumerada'),
                  dosisPeriodicidad: t('formularios.f005.dosisPeriodicidad'),
                  via: t('formularios.f005.viaAdministracion'),
                  cantidad: t('formularios.f005.cantidadTotal'),
                });
                const acumulados = [...paresRecetas, ...nuevos];
                setParesRecetas(acumulados);
                onGuardado([...paresNota, ...acumulados], datosEncabezadoMsp);
              }}
              onCancelar={() => setWizardAbierto(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
